import React, { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import styled from "styled-components";
import {
  Form,
  Input,
  InputNumber,
  Select,
  Button,
  message,
  Card,
  Tag,
  Spin,
  Alert,
} from "antd";
import {
  DollarOutlined,
  ClockCircleOutlined,
  ToolOutlined,
  CheckCircleOutlined,
  ExclamationCircleOutlined,
  HomeOutlined,
  PhoneOutlined,
  MailOutlined,
  UserOutlined,
  MessageOutlined,
  SendOutlined,
} from "@ant-design/icons";
import Logo from "../components/general/Logo";
import { device } from "../lib/breakpoints";
import { getApiUrl } from "../lib/api/config";
import LeoVerifiedBidBadge from "../components/vendor/LeoVerifiedBidBadge";
import PreferredEdgePromo from "../components/vendor/PreferredEdgePromo";

const { TextArea } = Input;
const { Option } = Select;

const VendorBidPage = () => {
  const { token } = useParams();
  const navigate = useNavigate();
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [ticketData, setTicketData] = useState(null);
  const [vendorData, setVendorData] = useState(null);
  const [existingBid, setExistingBid] = useState(null);
  const [error, setError] = useState(null);
  const [negotiationLog, setNegotiationLog] = useState([]);
  const [chatDraft, setChatDraft] = useState("");
  const [chatSending, setChatSending] = useState(false);
  const [isPreferredVendor, setIsPreferredVendor] = useState(false);

  const loadBidData = useCallback(async () => {
    if (!token) return;
    try {
      setLoading(true);
      setError(null);
      const apiUrl = getApiUrl();
      const response = await fetch(`${apiUrl}/vendor-bids/bid/${token}`);

      if (!response.ok) {
        if (response.status === 404) {
          setError(
            "Invalid or expired bid link. Please contact the property owner for a new link."
          );
        } else {
          setError("Failed to load ticket details. Please try again later.");
        }
        return;
      }

      const data = await response.json();
      setTicketData(data.ticket);
      setVendorData(data.vendor);
      setExistingBid(data.existingBid);
      setNegotiationLog(data.negotiation_log || []);
      setIsPreferredVendor(
        Boolean(data.isPreferredVendor ?? data.vendor?.isPreferred)
      );

      if (data.vendor) {
        form.setFieldsValue({
          vendorName: data.vendor.name,
          vendorEmail: data.vendor.email,
          vendorPhone: data.vendor.phone || "",
        });
      }
    } catch (err) {
      console.error("Error fetching ticket data:", err);
      setError("Network error. Please check your connection and try again.");
    } finally {
      setLoading(false);
    }
  }, [token, form]);

  useEffect(() => {
    loadBidData();
  }, [loadBidData]);

  const sendLeoMessage = async () => {
    const trimmed = chatDraft.trim();
    if (!trimmed || !token) return;
    try {
      setChatSending(true);
      const apiUrl = getApiUrl();
      const response = await fetch(`${apiUrl}/vendor-bids/bid/${token}/chat`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ message: trimmed }),
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) {
        throw new Error(data.error || "Could not send message");
      }
      setNegotiationLog(data.negotiation_log || []);
      setChatDraft("");
    } catch (err) {
      console.error(err);
      message.error(err.message || "Chat failed");
    } finally {
      setChatSending(false);
    }
  };

  const handleSubmit = async (values) => {
    try {
      setSubmitting(true);
      const apiUrl = getApiUrl();

      const bidData = {
        token,
        bidAmount: values.bidAmount,
        estimatedHours: values.estimatedHours,
        timeline: values.timeline,
        notes: values.notes,
        phone: values.vendorPhone,
      };

      const response = await fetch(`${apiUrl}/vendor-bids/bids`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(bidData),
      });

      const result = await response.json();

      if (!response.ok) {
        throw new Error(result.error || "Failed to submit bid");
      }

      message.success("Bid submitted successfully!");

      // Navigate to success page
      navigate("/bid/success", {
        state: {
          ticketTitle: ticketData.title,
          bidAmount: values.bidAmount,
          ticketId: ticketData?.id,
          workOrderId: ticketData?.id,
        },
      });
    } catch (err) {
      console.error("Error submitting bid:", err);
      message.error(err.message || "Failed to submit bid. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  const getPriorityColor = (priority) => {
    switch (priority?.toLowerCase()) {
      case "urgent":
        return "error";
      case "high":
        return "warning";
      case "medium":
        return "processing";
      case "low":
        return "default";
      default:
        return "default";
    }
  };

  const getBidStatusColor = (status) => {
    switch (status?.toLowerCase()) {
      case "accepted":
        return "success";
      case "rejected":
        return "error";
      case "pending":
        return "processing";
      default:
        return "default";
    }
  };

  if (loading) {
    return (
      <PageContainer>
        <LoadingContainer>
          <Spin size="large" />
          <LoadingText>Loading bid details...</LoadingText>
        </LoadingContainer>
      </PageContainer>
    );
  }

  if (error) {
    return (
      <PageContainer>
        <ErrorContainer>
          <Logo size="large" displayMode="both" />
          <Alert
            message="Unable to Load Bid"
            description={error}
            type="error"
            showIcon
            style={{ marginTop: "2rem", maxWidth: "600px" }}
          />
        </ErrorContainer>
      </PageContainer>
    );
  }

  if (existingBid) {
    return (
      <PageContainer>
        <VendorBidLayout>
          <MainCol>
        <Container>
          <Header>
            <Logo size="large" displayMode="both" />
            <HeaderTitle>Bid Already Submitted</HeaderTitle>
            <HeaderSubtitle>
              You've already submitted a bid for this repair
            </HeaderSubtitle>
          </Header>

          <Card
            title={
              <span>
                <MessageOutlined style={{ marginRight: 8 }} />
                Ask Leo AI
              </span>
            }
            extra={isPreferredVendor ? <LeoVerifiedBidBadge /> : null}
            style={{ marginBottom: "2rem" }}
          >
            <ChatScroll>
              {negotiationLog.length === 0 ? (
                <TextMuted>No messages in this thread yet.</TextMuted>
              ) : (
                negotiationLog.map((m, i) => (
                  <ChatBubble key={i} $role={m.role}>
                    <strong>{m.role}:</strong> {m.content}
                  </ChatBubble>
                ))
              )}
            </ChatScroll>
            <div style={{ display: "flex", gap: 8, marginTop: 12 }}>
              <TextArea
                rows={2}
                value={chatDraft}
                onChange={(e) => setChatDraft(e.target.value)}
                placeholder="Follow up with Leo AI about this job…"
                onPressEnter={(e) => {
                  if (!e.shiftKey) {
                    e.preventDefault();
                    sendLeoMessage();
                  }
                }}
              />
              <Button
                type="primary"
                icon={<SendOutlined />}
                loading={chatSending}
                onClick={sendLeoMessage}
                style={{ alignSelf: "flex-end" }}
              >
                Send
              </Button>
            </div>
          </Card>

          <Card>
            <ExistingBidSection>
              <SectionTitle>Your Existing Bid</SectionTitle>
              <BidStatusRow>
                <strong>Status:</strong>
                <Tag
                  color={getBidStatusColor(existingBid.status)}
                  style={{ marginLeft: "0.5rem" }}
                >
                  {existingBid.status.toUpperCase()}
                </Tag>
              </BidStatusRow>
              <BidDetailRow>
                <strong>Bid Amount:</strong> $
                {existingBid.bidAmount?.toLocaleString()}
              </BidDetailRow>
              {existingBid.estimatedHours && (
                <BidDetailRow>
                  <strong>Estimated Hours:</strong> {existingBid.estimatedHours}{" "}
                  hours
                </BidDetailRow>
              )}
              {existingBid.timeline && (
                <BidDetailRow>
                  <strong>Timeline:</strong> {existingBid.timeline}
                </BidDetailRow>
              )}
              {existingBid.notes && (
                <BidDetailRow>
                  <strong>Notes:</strong> {existingBid.notes}
                </BidDetailRow>
              )}
              <BidDetailRow>
                <strong>Submitted:</strong>{" "}
                {new Date(existingBid.createdAt).toLocaleString()}
              </BidDetailRow>
            </ExistingBidSection>
          </Card>
        </Container>
          </MainCol>
          <AsideCol>
            <PreferredEdgePromo
              bidToken={token}
              returnPage="bid"
              isPreferred={isPreferredVendor}
              onPreferredChange={setIsPreferredVendor}
            />
          </AsideCol>
        </VendorBidLayout>
      </PageContainer>
    );
  }

  return (
    <PageContainer>
      <VendorBidLayout>
        <MainCol>
      <Container>
        <Header>
          <Logo size="large" displayMode="both" />
          <HeaderTitle>Submit Your Bid</HeaderTitle>
          <HeaderSubtitle>New Maintenance Opportunity</HeaderSubtitle>
        </Header>

        {/* Ticket Details */}
        <Card title="Repair Details" style={{ marginBottom: "2rem" }}>
          <TicketDetailRow>
            <strong>
              <ToolOutlined /> Repair:
            </strong>{" "}
            {ticketData?.title}
          </TicketDetailRow>
          <TicketDetailRow>
            <strong>Category:</strong>
            <Tag style={{ marginLeft: "0.5rem" }}>{ticketData?.category}</Tag>
          </TicketDetailRow>
          <TicketDetailRow>
            <strong>Priority:</strong>
            <Tag
              color={getPriorityColor(ticketData?.priority)}
              style={{ marginLeft: "0.5rem" }}
            >
              {ticketData?.priority || "medium"}
            </Tag>
          </TicketDetailRow>
          {ticketData?.unitNumber && (
            <TicketDetailRow>
              <strong>
                <HomeOutlined /> Unit:
              </strong>{" "}
              {ticketData.unitNumber}
            </TicketDetailRow>
          )}
          {ticketData?.description && (
            <TicketDetailRow>
              <strong>Description:</strong>
              <Description>{ticketData.description}</Description>
            </TicketDetailRow>
          )}
          {ticketData?.estimatedCost > 0 && (
            <TicketDetailRow>
              <strong>
                <DollarOutlined /> Estimated Cost:
              </strong>{" "}
              ${ticketData.estimatedCost.toLocaleString()}
            </TicketDetailRow>
          )}
        </Card>

        {token && (
          <NegotiateLinkRow>
            <Link to={`/negotiate/${token}`}>
              Open Leo AI procurement chat (ZIP check, ETA & bid workflow)
            </Link>
          </NegotiateLinkRow>
        )}

        <Card
          title={
            <span>
              <MessageOutlined style={{ marginRight: 8 }} />
              Ask Leo AI about this job
            </span>
          }
          extra={isPreferredVendor ? <LeoVerifiedBidBadge /> : null}
          style={{ marginBottom: "2rem" }}
        >
          <p style={{ color: "var(--color-text-muted)", marginBottom: "1rem" }}>
            Leo AI uses the ticket description and property notes to answer
            questions before you submit your bid.
          </p>
          <ChatScroll>
            {negotiationLog.length === 0 ? (
              <TextMuted>No messages yet. Say hello or ask about access, scope, or materials.</TextMuted>
            ) : (
              negotiationLog.map((m, i) => (
                <ChatBubble key={i} $role={m.role}>
                  <strong>{m.role}:</strong> {m.content}
                </ChatBubble>
              ))
            )}
          </ChatScroll>
          <div style={{ display: "flex", gap: 8, marginTop: 12 }}>
            <TextArea
              rows={2}
              value={chatDraft}
              onChange={(e) => setChatDraft(e.target.value)}
              placeholder="e.g. Is the water shutoff accessible in-unit?"
              onPressEnter={(e) => {
                if (!e.shiftKey) {
                  e.preventDefault();
                  sendLeoMessage();
                }
              }}
            />
            <Button
              type="primary"
              icon={<SendOutlined />}
              loading={chatSending}
              onClick={sendLeoMessage}
              style={{ alignSelf: "flex-end" }}
            >
              Send
            </Button>
          </div>
        </Card>

        {/* Bid Submission Form */}
        <Card title="Your Bid">
          <Form
            form={form}
            layout="vertical"
            onFinish={handleSubmit}
            autoComplete="off"
          >
            {/* Vendor Information (Read-only) */}
            <FormSection>
              <SectionTitle>Vendor Information</SectionTitle>
              <Form.Item label="Company Name" name="vendorName">
                <Input prefix={<UserOutlined />} disabled />
              </Form.Item>
              <Form.Item label="Email" name="vendorEmail">
                <Input prefix={<MailOutlined />} disabled />
              </Form.Item>
              <Form.Item
                label="Phone Number"
                name="vendorPhone"
                rules={[
                  { required: true, message: "Please enter your phone number" },
                  {
                    pattern: /^[0-9+\-() ]+$/,
                    message: "Please enter a valid phone number",
                  },
                ]}
              >
                <Input
                  prefix={<PhoneOutlined />}
                  placeholder="(555) 123-4567"
                />
              </Form.Item>
            </FormSection>

            {/* Bid Details */}
            <FormSection>
              <SectionTitle>Bid Details</SectionTitle>
              <Form.Item
                label="Bid Amount"
                name="bidAmount"
                rules={[
                  { required: true, message: "Please enter your bid amount" },
                  {
                    type: "number",
                    min: 0,
                    message: "Bid amount must be positive",
                  },
                ]}
              >
                <InputNumber
                  prefix="$"
                  style={{ width: "100%" }}
                  placeholder="0.00"
                  formatter={(value) =>
                    `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ",")
                  }
                  parser={(value) => value.replace(/\$\s?|(,*)/g, "")}
                  min={0}
                  step={0.01}
                />
              </Form.Item>

              <Form.Item
                label="Estimated Hours (Optional)"
                name="estimatedHours"
              >
                <InputNumber
                  style={{ width: "100%" }}
                  placeholder="e.g., 4"
                  min={0}
                  step={0.5}
                />
              </Form.Item>

              <Form.Item label="Timeline" name="timeline">
                <Select placeholder="Select estimated timeline">
                  <Option value="Within 24 hours">Within 24 hours</Option>
                  <Option value="1-2 days">1-2 days</Option>
                  <Option value="2-3 days">2-3 days</Option>
                  <Option value="3-5 days">3-5 days</Option>
                  <Option value="1 week">1 week</Option>
                  <Option value="1-2 weeks">1-2 weeks</Option>
                </Select>
              </Form.Item>

              <Form.Item label="Additional Notes (Optional)" name="notes">
                <TextArea
                  rows={4}
                  placeholder="Describe your approach, materials to be used, warranty offered, or any other relevant details..."
                  maxLength={1000}
                  showCount
                />
              </Form.Item>
            </FormSection>

            <Form.Item>
              <SubmitButton
                type="primary"
                htmlType="submit"
                loading={submitting}
                icon={<CheckCircleOutlined />}
                block
                size="large"
              >
                Submit Bid
              </SubmitButton>
            </Form.Item>
          </Form>
        </Card>

        <Footer>
          <FooterText>
            After submitting your bid, the property owner will review it along
            with other bids. You'll be notified via email when a decision is
            made.
          </FooterText>
        </Footer>
      </Container>
        </MainCol>
        <AsideCol>
          <PreferredEdgePromo
            bidToken={token}
            returnPage="bid"
            isPreferred={isPreferredVendor}
            onPreferredChange={setIsPreferredVendor}
          />
        </AsideCol>
      </VendorBidLayout>
    </PageContainer>
  );
};

// Styled Components
const PageContainer = styled.div`
  min-height: 100vh;
  background: linear-gradient(135deg, #f5f7fa 0%, #e4e8ec 100%);
  padding: 2rem 1rem;

  @media ${device.mobile} {
    padding: 1rem 0.5rem;
  }
`;

const VendorBidLayout = styled.div`
  display: flex;
  gap: 1.5rem;
  align-items: flex-start;
  justify-content: center;
  max-width: 1120px;
  margin: 0 auto;
  flex-wrap: wrap;
`;

const MainCol = styled.div`
  flex: 1;
  min-width: 0;
  max-width: 800px;
`;

const AsideCol = styled.aside`
  width: 100%;
  max-width: 320px;
  flex-shrink: 0;
  position: sticky;
  top: 1rem;
  @media ${device.mobile} {
    position: static;
    max-width: none;
  }
`;

const Container = styled.div`
  width: 100%;
  margin: 0 auto;
`;

const LoadingContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 50vh;
  gap: 1.5rem;
`;

const LoadingText = styled.div`
  font-size: 1.1rem;
  color: var(--color-text-muted);
`;

const ErrorContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 50vh;
  padding: 2rem;
`;

const Header = styled.div`
  text-align: center;
  margin-bottom: 2rem;
  background: white;
  padding: 2rem;
  border-radius: 12px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
`;

const HeaderTitle = styled.h1`
  font-size: 2rem;
  font-weight: 700;
  color: var(--color-text);
  margin: 1rem 0 0.5rem 0;
  font-family: var(--font-primary);

  @media ${device.mobile} {
    font-size: 1.5rem;
  }
`;

const HeaderSubtitle = styled.p`
  font-size: 1.1rem;
  color: var(--color-text-muted);
  margin: 0;

  @media ${device.mobile} {
    font-size: 0.95rem;
  }
`;

const TicketDetailRow = styled.div`
  margin-bottom: 1rem;
  font-size: 1rem;
  color: var(--color-text);
  display: flex;
  align-items: flex-start;
  gap: 0.5rem;

  strong {
    min-width: 120px;
    color: var(--color-text);
  }

  @media ${device.mobile} {
    flex-direction: column;
    gap: 0.25rem;

    strong {
      min-width: auto;
    }
  }
`;

const Description = styled.div`
  white-space: pre-wrap;
  line-height: 1.6;
  color: var(--color-text-muted);
  margin-top: 0.5rem;
  padding: 1rem;
  background: #f5f5f5;
  border-radius: 8px;
`;

const FormSection = styled.div`
  margin-bottom: 2rem;
  padding-bottom: 2rem;
  border-bottom: 1px solid #f0f0f0;

  &:last-of-type {
    border-bottom: none;
  }
`;

const SectionTitle = styled.h3`
  font-size: 1.2rem;
  font-weight: 600;
  color: var(--color-text);
  margin-bottom: 1.5rem;
  font-family: var(--font-primary);
`;

const SubmitButton = styled(Button)`
  height: 50px;
  font-size: 1.1rem;
  font-weight: 600;
  background: var(--color-primary);
  border-color: var(--color-primary);

  &:hover {
    background: var(--color-primary-darker);
    border-color: var(--color-primary-darker);
  }
`;

const Footer = styled.div`
  margin-top: 2rem;
  text-align: center;
`;

const FooterText = styled.p`
  font-size: 0.9rem;
  color: var(--color-text-muted);
  line-height: 1.6;
  max-width: 600px;
  margin: 0 auto;
`;

const ExistingBidSection = styled.div`
  padding: 1rem;
`;

const BidStatusRow = styled.div`
  margin-bottom: 1rem;
  font-size: 1.1rem;
  display: flex;
  align-items: center;
`;

const BidDetailRow = styled.div`
  margin-bottom: 0.75rem;
  font-size: 1rem;
  color: var(--color-text-muted);

  strong {
    color: var(--color-text);
  }
`;

const ChatScroll = styled.div`
  max-height: 280px;
  overflow-y: auto;
  padding: 12px;
  background: #fafafa;
  border-radius: 8px;
  margin-bottom: 8px;
`;

const ChatBubble = styled.div`
  margin-bottom: 10px;
  font-size: 0.95rem;
  line-height: 1.5;
  padding: 8px 10px;
  border-radius: 8px;
  background: ${(p) =>
    p.$role === "leo"
      ? "#e6f4ff"
      : p.$role === "vendor"
      ? "#f6ffed"
      : "#f5f5f5"};
`;

const TextMuted = styled.p`
  margin: 0;
  color: var(--color-text-muted);
  font-size: 0.95rem;
`;

const NegotiateLinkRow = styled.div`
  text-align: center;
  margin-bottom: 1.25rem;
  a {
    font-weight: 600;
    color: var(--color-primary);
  }
`;

export default VendorBidPage;
